// The "Built on Cloudflare" banner shown inline below the header on every
// page. It states that all of Flarecade is built on and runs on Cloudflare
// Workers, and shows PER-PAGE live stats from Workers Analytics Engine via
// /api/stats: this page's view count, the average time Workers took to
// invoke, build, and serve this page (the "build & serve" stat), plus this
// visitor's own measured in-browser load time. It closes with a serverless
// tagline.
//
// All values are rendered with textContent (never innerHTML), so no data can
// inject markup. The only network call is a same-origin GET to /api/stats,
// which the page CSP (connect-src 'self') already permits.

import type { FlarecadeStats } from "../shared/analytics";
import "./cloudflare-banner.css";

const BUILT_TEXT = "Everything you see, hear, and do on Flarecade was built 100% on ";
const TAGLINE = "Zero servers. Zero idle compute. This code only runs when you're here.";

interface StatValueRefs {
  views: HTMLSpanElement;
  buildServe: HTMLSpanElement;
  thisPage: HTMLSpanElement;
}

export function initCloudflareBanner(): void {
  if (document.getElementById("cf-banner")) return;

  const { banner, refs } = buildBanner();

  const header = document.querySelector("header");
  if (header?.parentNode) {
    header.parentNode.insertBefore(banner, header.nextSibling);
  } else {
    document.body.insertBefore(banner, document.body.firstChild);
  }

  measureThisPage(refs.thisPage);
  void loadStats(refs);
}

function buildBanner(): { banner: HTMLElement; refs: StatValueRefs } {
  const banner = document.createElement("section");
  banner.id = "cf-banner";
  banner.className = "cf-banner";
  banner.setAttribute("aria-label", "Built on Cloudflare");

  const built = document.createElement("p");
  built.className = "cf-banner__built";
  built.appendChild(document.createTextNode(BUILT_TEXT));
  const mark = document.createElement("span");
  mark.className = "cf-banner__mark";
  mark.textContent = "Cloudflare";
  built.appendChild(mark);
  built.appendChild(document.createTextNode(" and runs on Cloudflare Workers."));

  const stats = document.createElement("p");
  stats.className = "cf-banner__stats";
  stats.setAttribute("role", "status");
  stats.setAttribute("aria-live", "polite");

  const views = statItem(stats, "views this page");
  const buildServe = statItem(stats, "ms to build & serve");
  const thisPage = statItem(stats, "ms in your browser");

  const tagline = document.createElement("p");
  tagline.className = "cf-banner__tagline";
  tagline.textContent = TAGLINE;

  banner.appendChild(built);
  banner.appendChild(stats);
  banner.appendChild(tagline);
  return { banner, refs: { views, buildServe, thisPage } };
}

function statItem(container: HTMLElement, label: string): HTMLSpanElement {
  const wrap = document.createElement("span");
  wrap.className = "cf-banner__stat";

  const value = document.createElement("span");
  value.className = "cf-banner__value";
  value.dataset.loading = "true";
  value.textContent = "…";

  const labelEl = document.createElement("span");
  labelEl.className = "cf-banner__label";
  labelEl.textContent = label;

  wrap.appendChild(value);
  wrap.appendChild(labelEl);
  container.appendChild(wrap);
  return value;
}

function setValue(el: HTMLSpanElement, text: string): void {
  el.textContent = text;
  el.dataset.loading = "false";
}

/** Measure this visitor's full navigation load time via the Performance API. */
function measureThisPage(el: HTMLSpanElement): void {
  const finish = (): void => {
    const nav = performance.getEntriesByType("navigation")[0] as
      | PerformanceNavigationTiming
      | undefined;
    let ms: number;
    if (nav && nav.loadEventEnd > 0) {
      ms = nav.loadEventEnd - nav.startTime;
    } else if (nav && nav.responseEnd > 0) {
      ms = nav.responseEnd - nav.startTime;
    } else {
      ms = performance.now();
    }
    setValue(el, String(Math.max(0, Math.round(ms))));
  };

  if (document.readyState === "complete") {
    finish();
  } else {
    window.addEventListener("load", () => window.setTimeout(finish, 0), { once: true });
  }
}

/** The path Analytics Engine recorded for this page (see worker.ts writeDataPoint). */
function currentPath(): string {
  return window.location.pathname || "/";
}

/** Match the current page against a perPage row, tolerating a trailing-slash difference. */
function matchPage(
  perPage: FlarecadeStats["perPage"],
  path: string,
): FlarecadeStats["perPage"][number] | undefined {
  const variants = new Set<string>([path]);
  if (path === "/") variants.add("/index.html");
  if (path.endsWith("/") && path.length > 1) variants.add(path.slice(0, -1));
  else variants.add(`${path}/`);
  return perPage.find((row) => variants.has(row.page));
}

async function loadStats(refs: StatValueRefs): Promise<void> {
  try {
    const response = await fetch("/api/stats", {
      headers: { accept: "application/json" },
    });
    if (!response.ok) throw new Error(`stats ${response.status}`);
    const stats = (await response.json()) as FlarecadeStats;
    if (!stats.available) {
      setValue(refs.views, "—");
      setValue(refs.buildServe, "—");
      return;
    }
    const row = matchPage(stats.perPage, currentPath());
    if (!row) {
      // No data ingested for this page yet (this very visit is still in flight).
      setValue(refs.views, formatCount(0));
      setValue(refs.buildServe, "—");
      return;
    }
    setValue(refs.views, formatCount(row.views));
    setValue(refs.buildServe, formatMs(row.avgLatencyMs));
  } catch {
    setValue(refs.views, "—");
    setValue(refs.buildServe, "—");
  }
}

function formatCount(n: number): string {
  if (!Number.isFinite(n) || n < 0) return "—";
  return Math.round(n).toLocaleString("en-US");
}

function formatMs(n: number): string {
  if (!Number.isFinite(n) || n < 0) return "—";
  return n < 10 ? n.toFixed(1) : String(Math.round(n));
}
